import React, { useState, useEffect, useCallback } from "react";
import { RefreshCw, BarChart3 } from "lucide-react";
import axios from "axios";
import { API } from "../../config";
import { useAuth } from "../../AuthContext";

/** McpUsageCard — per-key daily MCP request charts (last 14 days). */
export default function McpUsageCard({ dm }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { authHeader } = useAuth();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/admin/mcp-usage?days=14`, { headers: authHeader() });
      setData(d);
    } catch {
      // silent — card shows empty state
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);

  const card = `rounded-2xl p-6 ${dm ? "bg-[#1a2122]" : "bg-white"}`;
  const cardShadow = { boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 4px 24px rgba(25,28,29,0.04)" };
  const tierColor = (tier) =>
    tier === "admin" ? "#f59e0b" : tier === "pro" ? "#a855f7" : "#22d3ee";

  const keys = data?.keys || [];
  const days = data?.days || [];

  return (
    <div className={card} style={cardShadow} data-testid="mcp-usage-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className={`text-sm font-semibold flex items-center gap-2 ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>
          <BarChart3 className="w-4 h-4" /> MCP Usage — last 14 days
        </h3>
        <div className="flex items-center gap-2">
          {data && (
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${dm ? "bg-slate-700 text-slate-300" : "bg-slate-100 text-slate-600"}`} data-testid="mcp-usage-total">
              {data.total_requests} requests
            </span>
          )}
          <button
            onClick={load}
            data-testid="mcp-usage-refresh-btn"
            className={`p-1.5 rounded-lg transition-colors ${dm ? "text-slate-500 hover:bg-white/5" : "text-[#6b7a7d] hover:bg-slate-100"}`}
            title="Refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-6">
          <RefreshCw className={`w-4 h-4 animate-spin ${dm ? "text-cyan-400" : "text-[#006874]"}`} />
        </div>
      ) : keys.length === 0 ? (
        <p className={`text-xs text-center py-6 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
          No MCP usage recorded yet.
        </p>
      ) : (
        <div className="space-y-4">
          {keys.map((k) => {
            const max = Math.max(1, ...days.map((d) => k.daily[d] || 0));
            const color = tierColor(k.tier);
            return (
              <div key={k.id || k.name} data-testid={`mcp-usage-row-${k.id || "deleted"}`}>
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  <span className={`text-xs font-medium ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>{k.name}</span>
                  {k.tier && (
                    <span className="px-1.5 py-0.5 rounded-full text-[9px] font-mono font-semibold" style={{ background: `${color}22`, color }}>
                      {k.tier}
                    </span>
                  )}
                  {!k.is_active && (
                    <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-semibold ${dm ? "bg-slate-700 text-slate-400" : "bg-slate-100 text-slate-500"}`}>
                      inactive
                    </span>
                  )}
                  <span className={`ml-auto text-[11px] font-mono ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
                    {k.total} req
                  </span>
                </div>
                <div className="flex items-end gap-[3px]" style={{ height: 36 }} aria-label={`Daily requests for ${k.name}`}>
                  {days.map((d) => {
                    const v = k.daily[d] || 0;
                    return (
                      <div
                        key={d}
                        title={`${d}: ${v} request${v === 1 ? "" : "s"}`}
                        className="flex-1 rounded-sm transition-opacity hover:opacity-70"
                        style={{
                          height: v === 0 ? 2 : Math.max(4, Math.round((v / max) * 36)),
                          background: v === 0 ? (dm ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)") : color,
                        }}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
          <div className={`flex justify-between text-[9px] font-mono ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>
            <span>{days[0]}</span>
            <span>{days[days.length - 1]}</span>
          </div>
        </div>
      )}
    </div>
  );
}
